/**
 * C3 — domínio do Técnico (Corte 1: Início, Disponíveis, Detalhe da oferta, Agenda).
 * Não importa Vue/Pinia/HTTP: recebe dado já validado pelos schemas Zod de C4.
 */

/** Status canônicos de chamado (EMaintenanceStatus da tech-api, por StatusId). */
export type MaintenanceStatus =
  | 'PENDENTE'
  | 'ACEITA'
  | 'A_CAMINHO'
  | 'NO_LOCAL'
  | 'EM_ANDAMENTO'
  | 'AGUARDANDO_PAGAMENTO'
  | 'CONCLUIDA'
  | 'CANCELADA';

export const STATUS_BY_ID: Readonly<Record<number, MaintenanceStatus>> = {
  1: 'PENDENTE',
  5: 'ACEITA',
  6: 'A_CAMINHO',
  7: 'NO_LOCAL',
  2: 'EM_ANDAMENTO',
  8: 'AGUARDANDO_PAGAMENTO',
  3: 'CONCLUIDA',
  4: 'CANCELADA',
};

const STATUS_LABEL: Record<MaintenanceStatus, string> = {
  PENDENTE: 'Pendente',
  ACEITA: 'Aceito',
  A_CAMINHO: 'A caminho',
  NO_LOCAL: 'No local',
  EM_ANDAMENTO: 'Em andamento',
  AGUARDANDO_PAGAMENTO: 'Aguardando pagamento',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
};

export function maintenanceStatusLabel(status: MaintenanceStatus): string {
  switch (status) {
    case 'PENDENTE': return STATUS_LABEL.PENDENTE;
    case 'ACEITA': return STATUS_LABEL.ACEITA;
    case 'A_CAMINHO': return STATUS_LABEL.A_CAMINHO;
    case 'NO_LOCAL': return STATUS_LABEL.NO_LOCAL;
    case 'EM_ANDAMENTO': return STATUS_LABEL.EM_ANDAMENTO;
    case 'AGUARDANDO_PAGAMENTO': return STATUS_LABEL.AGUARDANDO_PAGAMENTO;
    case 'CONCLUIDA': return STATUS_LABEL.CONCLUIDA;
    case 'CANCELADA': return STATUS_LABEL.CANCELADA;
  }
}

export type UrgencyLevel = 'URGENTE' | 'AGENDADA';
export type CoverageCode = 'COBERTO' | 'EXCEDENTE';

/** Estados financeiros emitidos pelo demonstrativo aditivo do chamado. Estados podem coexistir
 *  porque uma parte do valor pode estar recebida enquanto outra continua pendente. */
export const TECHNICIAN_FINANCIAL_STATES = [
  'EXPECTED',
  'PENDING',
  'COMMISSION_PENDING',
  'RECEIVED',
  'RECEIVED_DIRECT',
  'REFUNDED',
  'DISPUTED',
] as const;

export type TechnicianFinancialState = (typeof TECHNICIAN_FINANCIAL_STATES)[number];
export type TechnicianFinancialSource = 'MAINTENANCE_PAYMENT' | 'EXTERNAL_CASH' | 'LEGACY_SNAPSHOT';

export interface TechnicianFinancialItem {
  readonly maintenanceRequestId: string;
  readonly source: TechnicianFinancialSource;
  readonly paymentId: string | null;
  /** Código do pagamento conciliado; livre porque é fornecido pelo gateway. */
  readonly paymentStatus: string | null;
  /** Código de repasse; o label amigável fica no extrato de repasses. */
  readonly payoutStatus: string | null;
  readonly payoutMethod: string | null;
  readonly grossAmount: number;
  readonly platformCommissionAmount: number;
  readonly gatewayFee: number | null;
  /** A tarifa só pode ser exibida como valor quando a fonte a conciliou. */
  readonly gatewayFeeKnown: boolean;
  readonly expectedAmount: number;
  readonly pendingAmount: number;
  readonly receivedAmount: number;
  readonly receivedDirectAmount: number;
  readonly refundedAmount: number;
  readonly disputedAmount: number;
  readonly occurredAt: string | null;
  readonly payoutPaidAt: string | null;
  readonly reversedAt: string | null;
  readonly isComplete: boolean;
  /** Evidências ausentes da parcela, enviadas pelo ledger sem converter pendência em baixa. */
  readonly missingEvidence: readonly string[];
  readonly disputedAt: string | null;
}

/** Componentes da cobrança resolvidos no servidor a partir dos snapshots e da OS.
 *  `null` significa fonte histórica ausente, nunca R$ 0,00. */
export interface TechnicianChargeComposition {
  readonly serviceAmountDue: number | null;
  readonly urgencyAmountDue: number | null;
  readonly partsAmountDue: number | null;
  readonly platformFundedAmount: number | null;
  readonly clinicAmountDue: number | null;
  /** Nulo enquanto uma resposta de rollout ainda não informa a completude da composição. */
  readonly isComplete: boolean | null;
  readonly missingComponents: readonly string[];
}

/** Projeção de leitura: não cria pagamento nem interpreta uma previsão como baixa. */
export interface TechnicianFinancialSummary {
  readonly expectedAmount: number;
  readonly pendingAmount: number;
  readonly receivedAmount: number;
  readonly receivedDirectAmount: number;
  readonly refundedAmount: number;
  readonly disputedAmount: number;
  readonly gatewayFee: number | null;
  readonly gatewayFeeKnown: boolean;
  readonly isComplete: boolean;
  readonly states: readonly TechnicianFinancialState[];
  readonly items: readonly TechnicianFinancialItem[];
  /** Composição única do chamado; não é repetida por parcela de pagamento. */
  readonly chargeComposition: TechnicianChargeComposition | null;
  /** Evidências que impedem apresentar o demonstrativo como totalmente conciliado. */
  readonly incompletenessReasons: readonly string[];
}

export function technicianFinancialStateLabel(state: TechnicianFinancialState): string {
  switch (state) {
    case 'EXPECTED': return 'Previsto';
    case 'PENDING': return 'A receber';
    case 'COMMISSION_PENDING': return 'Comissão pendente';
    case 'RECEIVED': return 'Recebido';
    case 'RECEIVED_DIRECT': return 'Recebido diretamente';
    case 'REFUNDED': return 'Estornado';
    case 'DISPUTED': return 'Em disputa';
  }
}

/** Label de cobertura exibido nos badges/PlanBand — mapa exaustivo da união (B1). */
export function coverageLabel(code: CoverageCode): string {
  switch (code) {
    case 'COBERTO': return 'COBERTO PELO PLANO';
    case 'EXCEDENTE': return 'FORA DA COTA';
  }
}

/** Item de lista de chamado/oferecimento (MaintenanceListItem da tech-api). */
export interface MaintenanceItem {
  readonly id: string;
  readonly equipmentName: string;
  readonly problemDescription: string;
  readonly requesterName: string;
  readonly status: MaintenanceStatus;
  readonly urgency: UrgencyLevel | null;
  readonly companyName: string | null;
  readonly companyCity: string | null;
  readonly companyState: string | null;
  readonly distanceKm: number | null;
  readonly grossAmount: number | null;
  readonly platformFeeAmount: number | null;
  /** Valor líquido previsto para o técnico, persistido no fechamento comercial. Este campo
   *  substitui o legado `netAmount` quando ambos existem; ele não comprova liquidação. */
  readonly technicianReceivableAmount: number | null;
  readonly netAmount: number | null;
  readonly planCode: string | null;
  readonly coverage: CoverageCode | null;
  readonly exclusiveUntil: string | null;
  readonly isPreferredOffer: boolean;
}

/** Detalhe de chamado/oferta (MaintenanceRequestResponse) — subconjunto usado pelo Corte 1. */
export interface MaintenanceDetail {
  readonly id: string;
  readonly companyId: string;
  readonly companyName: string;
  readonly requesterName: string;
  readonly equipmentName: string;
  readonly problemDescription: string;
  readonly status: MaintenanceStatus;
  readonly urgency: UrgencyLevel | null;
  readonly scheduledAt: string | null;
  readonly createdAt: string;
  readonly requestNumber: string | null;
  readonly companyAddressLine1: string | null;
  readonly companyAddressLine2: string | null;
  readonly companyCity: string | null;
  readonly companyState: string | null;
  readonly companyLatitude: number | null;
  readonly companyLongitude: number | null;
  /** Distância até a clínica medida pelo backend (B8) — null quando não medida. */
  readonly distanceKm: number | null;
  readonly clientAverageRating: number;
  readonly clientRatingsCount: number;
  readonly grossAmount: number | null;
  readonly platformFeeAmount: number | null;
  /** Valor líquido previsto para o técnico. A baixa financeira é informada pelo demonstrativo
   *  aditivo, portanto este número jamais deve ser rotulado como valor já recebido. */
  readonly technicianReceivableAmount: number | null;
  readonly netAmount: number | null;
  readonly planCode: string | null;
  readonly coverage: CoverageCode | null;
  readonly exclusiveUntil: string | null;
  readonly photos: readonly MaintenancePhoto[];
  /** Serial do equipamento vinculado (equipment_id) — null em chamados legados. */
  readonly equipmentSerialNumber: string | null;
  /** Comentário do recebimento em dinheiro (CashPaymentNote) — null sem confirmação em espécie. */
  readonly paymentComment: string | null;
  // ── Corte 2.1 — fluxo de atendimento (aditivos nullish; WhenWritingNull ⇒ ausentes no JSON) ──
  /** Cursor de retomada (screenCode gravado pelo técnico durante o atendimento). */
  readonly technicianCurrentScreenCode: string | null;
  readonly paymentMethodName: string | null;
  /** Modalidade comercial do chamado (FIXO | SIMBOLICO | …). */
  readonly commercialModelSnapshot: string | null;
  readonly openingFeeSnapshot: number | null;
  readonly commissionPercentage: number | null;
  /** Valor final confirmado — null antes da confirmação (AGUARDANDO_PAGAMENTO). */
  readonly finalValue: number | null;
  /** Valor ofertado na abertura do chamado — 0 quando o contrato não o enviar. */
  readonly serviceValueOffered: number;
  readonly minimumServiceValue: number | null;
  readonly equipmentCategoryId: number | null;
  readonly equipmentCategoryName: string | null;
  readonly technicianEquipmentCategoryId: number | null;
  readonly technicianEquipmentCategoryName: string | null;
  /** Demonstrativo conciliado; ausente em registros legados e antes do backend novo publicar. */
  readonly financialSummary: TechnicianFinancialSummary | null;
}

/**
 * Fonte única da previsão exibida ao técnico durante a transição de contrato. A API nova grava
 * `technicianReceivableAmount`; `netAmount` permanece somente como compatibilidade com respostas
 * legadas. Nenhuma das duas fontes significa que o repasse foi liquidado.
 */
export function technicianExpectedAmount(
  maintenance: Pick<MaintenanceItem, 'technicianReceivableAmount' | 'netAmount'>,
): number | null {
  return maintenance.technicianReceivableAmount ?? maintenance.netAmount;
}

/**
 * O demonstrativo conciliado é a fonte financeira autoritativa. Snapshots do chamado só servem
 * como previsão transitória para históricos sem `financialSummary`; eles nunca sobrepõem a tarifa,
 * o recebimento parcial ou outra composição já conciliada pelo servidor.
 */
export function technicianFinancialExpectedAmount(
  maintenance: Pick<MaintenanceDetail, 'financialSummary' | 'technicianReceivableAmount' | 'netAmount'>,
): number | null {
  return maintenance.financialSummary?.expectedAmount ?? technicianExpectedAmount(maintenance);
}

/** O chamado é da modalidade SIMBÓLICO (ato social) — muda o contexto de valor no fluxo. */
export function isSimbolico(detail: MaintenanceDetail): boolean {
  return detail.commercialModelSnapshot === 'SIMBOLICO';
}

/** Preço "de tabela" efetivo: o final confirmado; senão o ofertado; senão o mínimo vigente. */
export function effectiveTabledPrice(detail: MaintenanceDetail): number | null {
  if (detail.finalValue !== null && detail.finalValue > 0) return detail.finalValue;
  if (detail.serviceValueOffered > 0) return detail.serviceValueOffered;
  if (detail.minimumServiceValue !== null && detail.minimumServiceValue > 0) return detail.minimumServiceValue;
  return null;
}

export type AgendaItemKind = 'VISTORIA' | 'CHECKUP' | 'EMPRESTIMO';
export type DeadlineState = 'HOJE' | 'NO_PRAZO' | 'ATRASADO' | 'SEM_PRAZO';

/** Tipos de foto do atendimento — ANTES/DEPOIS + comprovantes (enum do backend EMaintenancePhotoType). */
export type MaintenancePhotoType =
  | 'ANTES'
  | 'DEPOIS'
  | 'COMPROVANTE_PIX'
  | 'COMPROVANTE_PAGAMENTO'
  | 'COMPROVANTE_REPASSE';

/** Tipos aceitos no UPLOAD — o backend valida ANTES, DEPOIS, COMPROVANTE_PIX ou COMPROVANTE_PAGAMENTO. */
export type MaintenancePhotoUploadType = 'ANTES' | 'DEPOIS' | 'COMPROVANTE_PIX' | 'COMPROVANTE_PAGAMENTO';

/** Foto do atendimento (MaintenancePhotoResponse) — ANTES | DEPOIS | comprovantes. */
export interface MaintenancePhoto {
  readonly id: string;
  readonly photoType: MaintenancePhotoType;
  /** Rota do proxy autenticado — nunca exposta como `<img src>` direto (exige Authorization). */
  readonly contentUrl: string;
}

export interface AgendaItem {
  readonly kind: AgendaItemKind;
  readonly id: string;
  readonly title: string;
  readonly companyName: string | null;
  readonly scheduledAt: string | null;
  readonly dueAt: string | null;
  readonly deadlineState: DeadlineState;
  readonly equipmentCount: number | null;
  readonly route: string;
}

export interface Agenda {
  readonly items: readonly AgendaItem[];
  readonly totalCount: number;
  readonly overdueCount: number;
  readonly todayCount: number;
}

export type TechPendencyCode = 'RECEBIMENTO' | 'CERTIFICACAO_VENCIDA' | 'CERTIFICACAO_A_VENCER';

export interface TechPendency {
  readonly code: TechPendencyCode;
  readonly message: string;
  readonly route: string;
}

/** Agregador da Home (TEL-01) — GET /technicians/{id}/app-summary. */
export interface TechAppSummary {
  readonly isAvailable: boolean;
  readonly monthlyNetEarnings: number;
  readonly monthlyServiceCount: number;
  readonly averageRating: number;
  readonly resumableService: MaintenanceItem | null;
  readonly activeServiceCount: number;
  readonly agendaPreview: readonly AgendaItem[];
  readonly agendaTodayCount: number;
  readonly agendaOverdueCount: number;
  readonly nearbyOffers: readonly MaintenanceItem[];
  readonly availableOfferCount: number;
  readonly pendencies: readonly TechPendency[];
  /** Próximo repasse agendado — null quando não há repasse previsto. */
  readonly nextPayoutAmount: number | null;
  readonly nextPayoutDate: string | null;
}
